/* This file is auto-generated */

import type { ContractPromise } from '@polkadot/api-contract';
import type { ApiPromise } from '@polkadot/api';
import type { KeyringPair } from '@polkadot/keyring/types';
import type { GasLimit, Result } from '@727-ventures/typechain-types';
import type { QueryReturnType } from '@727-ventures/typechain-types';
import { queryOkJSON, handleReturnType } from '@727-ventures/typechain-types';
import { txSignAndSend } from '@727-ventures/typechain-types';
import type * as ArgumentTypes from '../types-arguments/capsule';
import type * as ReturnTypes from '../types-returns/capsule';
import type BN from 'bn.js';
import type { ReturnNumber } from '@727-ventures/typechain-types';
import { getTypeDescription } from './../shared/utils';
// eslint-disable-next-line
// @ts-ignore
import type { EventRecord } from '@polkadot/api/submittable';
import { decodeEvents } from '../shared/utils';
import DATA_TYPE_DESCRIPTIONS from '../data/capsule.json';
import EVENT_DATA_TYPE_DESCRIPTIONS from '../event-data/capsule.json';

export default class Methods {
	readonly __nativeContract: ContractPromise;
	readonly __keyringPair: KeyringPair;
	readonly __callerAddress: string;
	readonly __apiPromise: ApiPromise;

	constructor(apiPromise: ApiPromise, nativeContract: ContractPromise, keyringPair: KeyringPair) {
		this.__apiPromise = apiPromise;
		this.__nativeContract = nativeContract;
		this.__keyringPair = keyringPair;
		this.__callerAddress = keyringPair.address;
	}

	/**
	 * mint
	 *
	 * @param { ArgumentTypes.AccountId } account,
	 * @param { (string | number | BN) } amount,
	 * @returns { void }
	 */
	mint(account: ArgumentTypes.AccountId, amount: string | number | BN, __options: GasLimit) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'mint',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[account, amount],
			__options,
		);
	}

	/**
	 * burn
	 *
	 * @param { ArgumentTypes.AccountId } account,
	 * @param { (string | number | BN) } amount,
	 * @returns { void }
	 */
	burn(account: ArgumentTypes.AccountId, amount: string | number | BN, __options: GasLimit) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'burn',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[account, amount],
			__options,
		);
	}

	/**
	 * holders
	 *
	 * @returns { Result<Array<[ReturnTypes.AccountId, ReturnNumber]>, ReturnTypes.LangError> }
	 */
	holders(
		__options: GasLimit,
	): Promise<
		QueryReturnType<Result<Array<[ReturnTypes.AccountId, ReturnNumber]>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'holders',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(16, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * transfer
	 *
	 * @param { ArgumentTypes.AccountId } to,
	 * @param { (string | number | BN) } value,
	 * @param { Array<(number | string | BN)> } data,
	 * @returns { void }
	 */
	transfer(
		to: ArgumentTypes.AccountId,
		value: string | number | BN,
		data: Array<number | string | BN>,
		__options: GasLimit,
	) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'transfer',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[to, value, data],
			__options,
		);
	}

	/**
	 * transferFrom
	 *
	 * @param { ArgumentTypes.AccountId } from,
	 * @param { ArgumentTypes.AccountId } to,
	 * @param { (string | number | BN) } value,
	 * @param { Array<(number | string | BN)> } data,
	 * @returns { void }
	 */
	transferFrom(
		from: ArgumentTypes.AccountId,
		to: ArgumentTypes.AccountId,
		value: string | number | BN,
		data: Array<number | string | BN>,
		__options: GasLimit,
	) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'transferFrom',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[from, to, value, data],
			__options,
		);
	}

	/**
	 * renounceOwnership
	 *
	 * @returns { void }
	 */
	renounceOwnership(__options: GasLimit) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'ownable::renounceOwnership',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[],
			__options,
		);
	}

	/**
	 * owner
	 *
	 * @returns { Result<ReturnTypes.AccountId | null, ReturnTypes.LangError> }
	 */
	owner(
		__options: GasLimit,
	): Promise<QueryReturnType<Result<ReturnTypes.AccountId | null, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'ownable::owner',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(21, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * transferOwnership
	 *
	 * @param { ArgumentTypes.AccountId | null } newOwner,
	 * @returns { void }
	 */
	transferOwnership(newOwner: ArgumentTypes.AccountId | null, __options: GasLimit) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'ownable::transferOwnership',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[newOwner],
			__options,
		);
	}

	/**
	 * increaseAllowance
	 *
	 * @param { ArgumentTypes.AccountId } spender,
	 * @param { (string | number | BN) } deltaValue,
	 * @returns { void }
	 */
	increaseAllowance(
		spender: ArgumentTypes.AccountId,
		deltaValue: string | number | BN,
		__options: GasLimit,
	) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'psp22::increaseAllowance',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[spender, deltaValue],
			__options,
		);
	}

	/**
	 * approve
	 *
	 * @param { ArgumentTypes.AccountId } spender,
	 * @param { (string | number | BN) } value,
	 * @returns { void }
	 */
	approve(spender: ArgumentTypes.AccountId, value: string | number | BN, __options: GasLimit) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'psp22::approve',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[spender, value],
			__options,
		);
	}

	/**
	 * balanceOf
	 *
	 * @param { ArgumentTypes.AccountId } owner,
	 * @returns { Result<ReturnNumber, ReturnTypes.LangError> }
	 */
	balanceOf(
		owner: ArgumentTypes.AccountId,
		__options: GasLimit,
	): Promise<QueryReturnType<Result<ReturnNumber, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::balanceOf',
			[owner],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(23, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * allowance
	 *
	 * @param { ArgumentTypes.AccountId } owner,
	 * @param { ArgumentTypes.AccountId } spender,
	 * @returns { Result<ReturnNumber, ReturnTypes.LangError> }
	 */
	allowance(
		owner: ArgumentTypes.AccountId,
		spender: ArgumentTypes.AccountId,
		__options: GasLimit,
	): Promise<QueryReturnType<Result<ReturnNumber, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::allowance',
			[owner, spender],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(23, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * allowances
	 *
	 * @param { ArgumentTypes.AccountId } owner,
	 * @returns { Result<Array<[ReturnTypes.AccountId, ReturnNumber]>, ReturnTypes.LangError> }
	 */
	allowances(
		owner: ArgumentTypes.AccountId,
		__options: GasLimit,
	): Promise<
		QueryReturnType<Result<Array<[ReturnTypes.AccountId, ReturnNumber]>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::allowances',
			[owner],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(16, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * psp22::transfer
	 *
	 * @param { ArgumentTypes.AccountId } to,
	 * @param { (string | number | BN) } value,
	 * @param { Array<(number | string | BN)> } data,
	 * @returns { void }
	 */
	'psp22::transfer'(
		to: ArgumentTypes.AccountId,
		value: string | number | BN,
		data: Array<number | string | BN>,
		__options: GasLimit,
	) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'psp22::transfer',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[to, value, data],
			__options,
		);
	}

	/**
	 * psp22::transferFrom
	 *
	 * @param { ArgumentTypes.AccountId } from,
	 * @param { ArgumentTypes.AccountId } to,
	 * @param { (string | number | BN) } value,
	 * @param { Array<(number | string | BN)> } data,
	 * @returns { void }
	 */
	'psp22::transferFrom'(
		from: ArgumentTypes.AccountId,
		to: ArgumentTypes.AccountId,
		value: string | number | BN,
		data: Array<number | string | BN>,
		__options: GasLimit,
	) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'psp22::transferFrom',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[from, to, value, data],
			__options,
		);
	}

	/**
	 * decreaseAllowance
	 *
	 * @param { ArgumentTypes.AccountId } spender,
	 * @param { (string | number | BN) } deltaValue,
	 * @returns { void }
	 */
	decreaseAllowance(
		spender: ArgumentTypes.AccountId,
		deltaValue: string | number | BN,
		__options: GasLimit,
	) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'psp22::decreaseAllowance',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[spender, deltaValue],
			__options,
		);
	}

	/**
	 * totalSupply
	 *
	 * @returns { Result<ReturnNumber, ReturnTypes.LangError> }
	 */
	totalSupply(
		__options: GasLimit,
	): Promise<QueryReturnType<Result<ReturnNumber, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::totalSupply',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(23, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * domainSeparator
	 *
	 * @returns { void }
	 */
	domainSeparator(__options: GasLimit) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'psp22Permit::domainSeparator',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[],
			__options,
		);
	}

	/**
	 * permit
	 *
	 * @param { ArgumentTypes.AccountId } owner,
	 * @param { ArgumentTypes.AccountId } spender,
	 * @param { (string | number | BN) } value,
	 * @param { (number | string | BN) } deadline,
	 * @param { ArgumentTypes.Signature } signature,
	 * @returns { void }
	 */
	permit(
		owner: ArgumentTypes.AccountId,
		spender: ArgumentTypes.AccountId,
		value: string | number | BN,
		deadline: number | string | BN,
		signature: ArgumentTypes.Signature,
		__options: GasLimit,
	) {
		return txSignAndSend(
			this.__apiPromise,
			this.__nativeContract,
			this.__keyringPair,
			'psp22Permit::permit',
			(events: EventRecord) => {
				return decodeEvents(events, this.__nativeContract, EVENT_DATA_TYPE_DESCRIPTIONS);
			},
			[owner, spender, value, deadline, signature],
			__options,
		);
	}

	/**
	 * tokenDecimals
	 *
	 * @returns { Result<number, ReturnTypes.LangError> }
	 */
	tokenDecimals(
		__options: GasLimit,
	): Promise<QueryReturnType<Result<number, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Metadata::tokenDecimals',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(27, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * contractType
	 *
	 * @returns { Result<string | null, ReturnTypes.LangError> }
	 */
	contractType(
		__options: GasLimit,
	): Promise<QueryReturnType<Result<string | null, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Metadata::contractType',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(28, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * tokenName
	 *
	 * @returns { Result<string | null, ReturnTypes.LangError> }
	 */
	tokenName(
		__options: GasLimit,
	): Promise<QueryReturnType<Result<string | null, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Metadata::tokenName',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(28, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * tokenSymbol
	 *
	 * @returns { Result<string | null, ReturnTypes.LangError> }
	 */
	tokenSymbol(
		__options: GasLimit,
	): Promise<QueryReturnType<Result<string | null, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Metadata::tokenSymbol',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(28, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * nonces
	 *
	 * @param { ArgumentTypes.AccountId } account,
	 * @returns { Result<number, ReturnTypes.LangError> }
	 */
	nonces(
		account: ArgumentTypes.AccountId,
		__options: GasLimit,
	): Promise<QueryReturnType<Result<number, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'nonces::nonces',
			[account],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(30, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}
}
